import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Select, Button, Popconfirm, message, Card, Row, Col, Typography, Space,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import api from '../services/api';
import { canEdit } from '../utils/auth';
import { palette } from '../theme';
import Pill from '../components/Pill';

const REASON_OPTIONS = [
  { label: 'Физическое лицо', value: 'individual' },
  { label: 'Военное/боевое формирование', value: 'military_formation' },
  { label: 'Судно/воздушное судно', value: 'vessel_or_aircraft' },
  { label: 'Помечено источником как не-ЮЛ', value: 'marked_non_entity' },
  { label: 'Нет признаков ЮЛ (пустое/нераспознанное название)', value: 'too_short' },
];

const REASON_COLORS: Record<string, { bg: string; fg: string }> = {
  individual: { bg: '#E7EEF7', fg: palette.blue },
  military_formation: { bg: '#F6DEDA', fg: palette.red },
  vessel_or_aircraft: { bg: '#FCEFD9', fg: palette.orange },
  marked_non_entity: { bg: palette.border, fg: palette.muted },
  too_short: { bg: palette.border, fg: palette.muted },
};

// Реальный случай (список СовБеза ООН, "OFFICE 39" — госорган КНДР ложно
// отбракован как военное формирование из-за слова "division" внутри
// "Central Committee Bureau 39 ... Division 39"): раньше строки, не
// прошедшие фильтр "это ЮЛ" (физлица, военные/боевые формирования, суда),
// просто тихо исчезали при парсинге файла — без всякого способа узнать,
// что они вообще были в файле, и уж тем более вернуть их, если фильтр
// ошибся. Эта страница показывает ВСЕ такие строки с причиной отбраковки и
// позволяет восстановить конкретную как обычную карточку ЮЛ одним кликом.
const RejectedEntitiesPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [showRestored, setShowRestored] = useState(false);
  const [sourceFileId, setSourceFileId] = useState<number | undefined>(undefined);
  const [files, setFiles] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const editable = canEdit();

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get('/rejected-entities', {
      params: { reason, restored: showRestored, source_file_id: sourceFileId, page, page_size: 50 },
    })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => message.error('Ошибка загрузки отбракованных записей'))
      .finally(() => setLoading(false));
  }, [reason, showRestored, sourceFileId, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Список загруженных файлов — для фильтра "показать отбракованные только
  // из этого файла", чтобы не пролистывать все загрузки подряд после
  // очередной новой загрузки.
  useEffect(() => {
    api.get('/files/').then(r => setFiles(r.data)).catch(() => {});
  }, []);

  const fileOptions = files.map(f => ({ label: `#${f.id} — ${f.filename}`, value: f.id }));

  const handleRestore = async (id: number) => {
    try {
      await api.post(`/rejected-entities/${id}/restore`);
      message.success('Запись восстановлена как ЮЛ');
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Ошибка восстановления');
    }
  };

  const columns: any[] = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Название', dataIndex: 'display_name', width: 420,
      render: (v: string) => v ? <Typography.Text ellipsis={{ tooltip: v }} style={{ maxWidth: 400 }}>{v}</Typography.Text> : '—',
    },
    {
      title: 'Причина отбраковки', dataIndex: 'reason_label', width: 300,
      render: (label: string, rec: any) => {
        const c = REASON_COLORS[rec.reason] || { bg: palette.border, fg: palette.muted };
        return <Pill bg={c.bg} fg={c.fg}>{label}</Pill>;
      },
    },
    { title: 'Файл', dataIndex: 'source_file_id', width: 80, render: (v: number) => v ? `#${v}` : '—' },
    {
      title: 'Загружено', dataIndex: 'created_at', width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('ru-RU') : '—',
    },
    ...(editable && !showRestored ? [{
      title: 'Действия', key: 'actions', width: 160,
      render: (_: any, rec: any) => (
        <Popconfirm
          title={`Восстановить "${rec.display_name || rec.id}" как ЮЛ?`}
          description="Причина отбраковки могла быть ошибочной — запись создастся со статусом 'Требует проверки'."
          onConfirm={() => handleRestore(rec.id)}
          okText="Восстановить" cancelText="Отмена"
        >
          <Button size="small" type="primary" icon={<CheckOutlined />}>Восстановить</Button>
        </Popconfirm>
      ),
    }] : []),
    ...(showRestored ? [{
      title: 'Создано ЮЛ ID', dataIndex: 'restored_entity_id', width: 120,
      render: (v: number) => v ? `#${v}` : '—',
    }] : []),
  ];

  return (
    <div>
      <div className="eyebrow">Реестр</div>
      <Typography.Title level={3} className="page-title" style={{ marginTop: 0 }}>Отбракованные записи</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ maxWidth: 720 }}>
        Строки, которые парсер счёл НЕ юридическим лицом (физлицо, военное/боевое
        формирование, судно или воздушное судно) и не добавил в реестр ЮЛ. Если
        отбраковка ошибочна — восстановите запись как обычную карточку ЮЛ.
      </Typography.Paragraph>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Space>
              <Select
                allowClear showSearch placeholder="Файл" style={{ width: 320 }}
                options={fileOptions} value={sourceFileId}
                filterOption={(input, option) =>
                  (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                onChange={v => { setSourceFileId(v); setPage(1); }}
              />
              <Select
                allowClear placeholder="Причина" style={{ width: 340 }}
                options={REASON_OPTIONS} value={reason}
                onChange={v => { setReason(v); setPage(1); }}
              />
              <Select
                style={{ width: 200 }} value={showRestored}
                onChange={v => { setShowRestored(v); setPage(1); }}
                options={[
                  { label: 'Не восстановленные', value: false },
                  { label: 'Восстановленные', value: true },
                ]}
              />
            </Space>
          </Col>
        </Row>
      </Card>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={{
          current: page, pageSize: 50, total,
          onChange: setPage, showTotal: (t) => `Всего: ${t}`,
        }}
      />
    </div>
  );
};

export default RejectedEntitiesPage;
